import type { FunctionComponent } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import Heading from '@atlaskit/heading';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';

export interface DetailEntry {
  /** The label shown before the colon. */
  label: MessageDescriptor;
  /** A single string or an array of strings (rendered comma-separated). */
  value: string | string[];
  /** Whether the label should use the subtle Body/M field-label appearance. */
  useFieldLabelStyle?: boolean;
  /** Whether the value should use the subtle text color. */
  useSubtleValueColor?: boolean;
}

export interface DetailsProps {
  /** Section heading, e.g. "Details". */
  title: MessageDescriptor;
  /** Rows to render — each gets a label and value(s). */
  entries: DetailEntry[];
}

const detailsHeadingStyle = xcss({
  marginBottom: 'space.150',
});

const detailLabelStyle = xcss({
  width: 'size.600',
  flexShrink: 0,
});

const Details: FunctionComponent<DetailsProps> = ({ title, entries }) => {
  return (
    <Stack>
      <Box xcss={detailsHeadingStyle}>
        <Heading size="small">
          <FormattedMessage {...title} />
        </Heading>
      </Box>
      <Stack space="space.075">
        {entries.map((entry) => {
          const values = Array.isArray(entry.value)
            ? entry.value
            : [entry.value];

          if (values.length === 0) return null;

          return (
            <Flex key={entry.label.id} gap="space.050">
              <Box xcss={detailLabelStyle}>
                <Text
                  as="span"
                  weight={entry.useFieldLabelStyle ? 'medium' : 'bold'}
                  color={
                    entry.useFieldLabelStyle ? 'color.text.subtle' : undefined
                  }
                >
                  <FormattedMessage {...entry.label} />
                  {':'}
                </Text>
              </Box>
              <Stack space="space.025">
                {values.map((v) => (
                  <Text
                    key={v}
                    as="span"
                    color={
                      entry.useSubtleValueColor
                        ? 'color.text.subtle'
                        : undefined
                    }
                  >
                    {v}
                  </Text>
                ))}
              </Stack>
            </Flex>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default Details;
